import axios from 'axios';
import { API_BASE_URL } from '../config/constants';
import { Config, Parameter, ShareLinkRequest, ShareLinkResponse } from '../types/config';

export class ConfigService {
  private static readonly BASE_URL = `${API_BASE_URL}/api/configs`;

  static async getAllConfigs(): Promise<Config[]> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(this.BASE_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch configs:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch configurations');
    }
  }

  static async getActiveConfig(): Promise<Config | null> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${this.BASE_URL}/active`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to fetch active config:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch active configuration');
    }
  }

  static async getConfigById(id: string): Promise<Config> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${this.BASE_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch config:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch configuration');
    }
  }

  static async fetchConfigs(): Promise<Config[]> {
    return this.getAllConfigs();
  }

  static async saveConfigs(configs: Config[]): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      await axios.post(`${this.BASE_URL}/bulk`, configs, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error: any) {
      console.error('Failed to save configs:', error);
      throw new Error(error.response?.data?.message || 'Failed to save configurations');
    }
  }

  static async deleteConfig(id: string): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${this.BASE_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error: any) {
      console.error('Failed to delete config:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete configuration');
    }
  }

  static async createConfig(config: Omit<Config, 'createdAt' | 'updatedAt'>): Promise<Config> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(this.BASE_URL, config, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to create config:', error);
      throw new Error(error.response?.data?.message || 'Failed to create configuration');
    }
  }

  static async updateConfig(id: string, config: Partial<Config>): Promise<Config> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.put(`${this.BASE_URL}/${id}`, config, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to update config:', error);
      throw new Error(error.response?.data?.message || 'Failed to update configuration');
    }
  }

  static async generateShareLink(configId: string, request: ShareLinkRequest): Promise<ShareLinkResponse> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${this.BASE_URL}/${configId}/share`, request, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to generate share link:', error);
      throw new Error(error.response?.data?.message || 'Failed to generate share link');
    }
  }

  static async removeShareLink(configId: string): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(`${this.BASE_URL}/${configId}/share`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error: any) {
      console.error('Failed to remove share link:', error);
      throw new Error(error.response?.data?.message || 'Failed to remove share link');
    }
  }

  static async deleteShareLink(configId: string): Promise<void> {
    return this.removeShareLink(configId);
  }

  static async setActiveConfig(configId: string): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      await axios.put(`${this.BASE_URL}/${configId}/active`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error: any) {
      console.error('Failed to set active config:', error);
      throw new Error(error.response?.data?.message || 'Failed to set active configuration');
    }
  }

  static async getSharedConfig(shareId: string): Promise<Config> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${this.BASE_URL}/shared/${shareId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch shared config:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch shared configuration');
    }
  }

  static async fetchSharedConfig(shareId: string): Promise<Config> {
    return this.getSharedConfig(shareId);
  }

  static async validateShareLink(shareId: string): Promise<boolean> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${this.BASE_URL}/shared/${shareId}/validate`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.valid;
    } catch (error: any) {
      return false;
    }
  }

  static async fetchParameters(documentId: string): Promise<Parameter[]> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${API_BASE_URL}/api/proxy/parameters`, {
        documentId: documentId
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      // Transform the API response to our Parameter interface
      const apiData = response.data;
      const parameters: Parameter[] = [];
      
      if (apiData.user_input_form && Array.isArray(apiData.user_input_form)) {
        for (const formField of apiData.user_input_form) {
          // Each form field is an object with a single key (the field type)
          const fieldType = Object.keys(formField)[0];
          const fieldData = formField[fieldType];
          
          if (fieldData && !fieldData.hide) {
            // Map the field type to our supported types
            let mappedType: Parameter['type'] = 'text';
            switch (fieldType) {
              case 'text-input':
              case 'text':
                mappedType = 'text';
                break;
              case 'paragraph':
                mappedType = 'textarea';
                break;
              case 'number':
                mappedType = 'number';
                break;
              case 'select':
                mappedType = 'select';
                break;
              case 'checkbox':
                mappedType = 'checkbox';
                break;
              case 'file':
              case 'file-list':
                mappedType = 'file';
                break;
              default:
                mappedType = 'text';
            }
            
            // Process options for select fields
            let processedOptions: Parameter['options'] = undefined;
            if (fieldData.options && Array.isArray(fieldData.options) && fieldData.options.length > 0) {
              processedOptions = fieldData.options.map((opt: any) => {
                // Handle both string options and object options
                if (typeof opt === 'string') {
                  return { label: opt, value: opt };
                } else if (opt && typeof opt === 'object') {
                  return { 
                    label: opt.label || opt.value || opt.toString(), 
                    value: opt.value !== undefined ? opt.value : opt 
                  };
                } else {
                  return { label: opt?.toString() || 'Option', value: opt };
                }
              });
            }
            
            // Calculate file restrictions for file inputs
            let fileAccept = undefined;
            if (mappedType === 'file') {
              const fileTypes = fieldData.allowed_file_types || [];
              const fileExtensions = fieldData.allowed_file_extensions || [];
              
              if (fileExtensions.length > 0) {
                fileAccept = fileExtensions.join(',');
              } else if (fileTypes.length > 0) {
                // Map file types to MIME types/extensions
                const mimeMap: Record<string, string> = {
                  'image': '.jpg,.jpeg,.png,.gif,.webp,.svg',
                  'document': '.pdf,.doc,.docx,.txt,.rtf',
                  'audio': '.mp3,.wav,.m4a,.ogg',
                  'video': '.mp4,.avi,.mov,.wmv',
                  'text': '.txt,.md,.csv'
                };
                fileAccept = fileTypes.map((type: any) => mimeMap[type] || type).join(',');
              }
            }
            
            // Determine max files based on field type
            // 'file' type = single file (max 1), 'file-list' type = multiple files
            const isSingleFile = fieldType === 'file';
            const maxFilesForType = isSingleFile ? 1 : (fieldData.max_length || 1);

            parameters.push({
              variable: fieldData.variable || `field_${parameters.length}`,
              label: fieldData.label || 'Input',
              type: mappedType,
              required: fieldData.required || false,
              description: fieldData.description || undefined,
              placeholder: fieldData.placeholder || undefined,
              defaultValue: fieldData.default_value || undefined,
              min: fieldData.min_length || fieldData.min || undefined,
              max: fieldData.max_length || fieldData.max || undefined,
              // Add max_length for file parameters to use directly
              max_length: isSingleFile ? 1 : (fieldData.max_length || undefined),
              step: fieldData.step || (mappedType === 'number' ? 1 : undefined),
              options: processedOptions,
              accept: fileAccept,
              // Add custom metadata for advanced file handling
              metadata: {
                originalType: fieldType,
                allowedUploadMethods: fieldData.allowed_file_upload_methods || [],
                allowedFileTypes: fieldData.allowed_file_types || [],
                allowedFileExtensions: fieldData.allowed_file_extensions || [],
                maxFiles: maxFilesForType,
                fileUploadConfig: apiData.file_upload?.fileUploadConfig || {}
              }
            });
          }
        }
      }
      
      console.log('Transformed parameters:', parameters);
      return parameters;
    } catch (error: any) {
      console.error('Failed to fetch parameters:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch parameters for this document');
    }
  }
}